import React, { useContext, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import FbLogin from 'react-facebook-login';
import GoogleLogin from '@leecheuk/react-google-login';
import axiosInstance from '../../axios/login';
import FacebookLogin from '../../axios/facebookLogin';
import gLogin from '../../axios/googleLogin';
import UserAPI from '../../axios/user';
import { UserContext } from '../../contexts/userContext';
import '../../hs-theme.css';
import './auth-page.css';

const heroModule = require('../../assets/images/home.jpg');
const heroImage = heroModule.default || heroModule;

const FACEBOOK_APP_ID = '1322846145008621';
const GOOGLE_CLIENT_ID = '196596446612-6e8g7gcde4n26v5n3ekjphi9qev9vnpk.apps.googleusercontent.com';
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
const EMPTY = { email: '', password: '', username: '', first_name: '', last_name: '', password_confirm: '' };
const PERKS = ['perk_1', 'perk_2', 'perk_3'];

const ICONS = {
    mail: 'M3 6h18v12H3ZM3 7l9 6 9-6',
    at: 'M16 12a4 4 0 1 1-8 0 4 4 0 0 1 8 0Zm0 0v1.5a2.5 2.5 0 0 0 5 0V12a9 9 0 1 0-3.5 7.1',
    user: 'M12 4a4 4 0 1 0 0 8 4 4 0 0 0 0-8ZM4 21c1.5-4 4.5-6 8-6s6.5 2 8 6',
    lock: 'M6 11h12a1 1 0 0 1 1 1v8a1 1 0 0 1-1 1H6a1 1 0 0 1-1-1v-8a1 1 0 0 1 1-1ZM8 11V7a4 4 0 0 1 8 0v4',
    eye: 'M2.5 12S6 5 12 5s9.5 7 9.5 7-3.5 7-9.5 7-9.5-7-9.5-7ZM12 9a3 3 0 1 0 0 6 3 3 0 0 0 0-6Z',
    eyeOff: 'M10.7 5.1A9.8 9.8 0 0 1 12 5c6 0 9.5 7 9.5 7a17 17 0 0 1-2.7 3.4M6.6 6.6C3.9 8.4 2.5 12 2.5 12S6 19 12 19c1.9 0 3.6-.6 5-1.5M9.9 9.9a3 3 0 0 0 4.2 4.2M3 3l18 18',
    alert: 'M12 3a9 9 0 1 0 0 18 9 9 0 0 0 0-18ZM12 8v5M12 16h.01',
    check: 'm5 12.5 4.5 4.5L19 7.5',
};

const Icon = ({ d, size = 18, width = 1.9 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={width} strokeLinecap="round" strokeLinejoin="round"><path d={d} /></svg>
);

const GoogleIcon = () => (
    <svg width="18" height="18" viewBox="0 0 48 48"><path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.7 29.2 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.4-.4-3.5z" /><path fill="#FF3D00" d="m6.3 14.7 6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z" /><path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2c-2 1.5-4.5 2.4-7.2 2.4-5.2 0-9.6-3.3-11.3-8l-6.5 5C9.5 39.6 16.2 44 24 44z" /><path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.2-4.1 5.6l6.2 5.2C37 39.2 44 34 44 24c0-1.3-.1-2.4-.4-3.5z" /></svg>
);

const FacebookIcon = () => (
    <svg width="18" height="18" viewBox="0 0 24 24"><circle cx="12" cy="12" r="12" fill="#1877F2" /><path d="M13.4 19.5v-6h2l.3-2.4h-2.3V9.6c0-.7.2-1.2 1.2-1.2h1.2V6.3c-.2 0-1-.1-1.8-.1-1.8 0-3 1.1-3 3.1v1.8H9v2.4h2v6h2.4Z" fill="#fff" /></svg>
);

const passwordRules = (password, confirm) => [
    ['rule_length', password.length >= 8],
    ['rule_digit', /\d/.test(password)],
    ['rule_upper', /[A-ZŠĐČĆŽ]/.test(password)],
    ['rule_lower', /[a-zšđčćž]/.test(password)],
    ['rule_match', password.length > 0 && password === confirm],
];

const Field = ({ label, icon, type = 'text', half, error, ...input }) => {
    const { t } = useTranslation(['login']);
    const [shown, setShown] = useState(false);
    const isPassword = type === 'password';

    return (
        <div className={`hs-auth-field-wrap ${half ? 'half' : ''}`}>
            <label className={`hs-auth-field ${error ? 'error' : ''}`}>
                <span className='hs-auth-field-icon'><Icon d={icon} /></span>
                <span className='hs-auth-field-text'>
                    <span className='hs-auth-field-label'>{label}</span>
                    <input type={isPassword && shown ? 'text' : type} {...input} />
                </span>
                {isPassword &&
                    <button type='button' className='hs-auth-eye' aria-label={t(shown ? 'hide_password' : 'show_password')}
                        onClick={(e) => { e.preventDefault(); setShown(!shown); }}>
                        <Icon d={shown ? ICONS.eyeOff : ICONS.eye} />
                    </button>}
            </label>
            {error && <span className='hs-auth-field-error'>{error}</span>}
        </div>
    );
};

// login, registration and forgotten password share one card; `view` comes from the route
export const AuthPage = ({ view }) => {
    const { t } = useTranslation(['login', 'register']);
    const { setUser } = useContext(UserContext);
    const navigate = useNavigate();
    const location = useLocation();

    const [values, setValues] = useState(EMPTY);
    // errors are shown after the first submit attempt, then update as the visitor types
    const [tried, setTried] = useState(false);
    const [serverErrors, setServerErrors] = useState({});
    const [alert, setAlert] = useState(null);
    const [loading, setLoading] = useState(false);
    const [done, setDone] = useState(false);
    const [resent, setResent] = useState(null);

    const isLogin = view === 'login', isRegister = view === 'register', isForgot = view === 'forgot';
    const tr = (key) => t(key, { ns: 'register' });
    const email = values.email.trim();

    const handleChange = (e) => {
        const { name, value } = e.target;
        setValues({ ...values, [name]: value });
        setServerErrors({ ...serverErrors, [name]: undefined });
        setAlert(null);
    };

    const validate = () => {
        const errors = {};
        if (!email) errors.email = t('email_required');
        else if (!EMAIL_RE.test(email)) errors.email = tr('email_not_valid');
        if (isLogin && !values.password) errors.password = t('password_required');
        if (isRegister) {
            if (!values.first_name.trim()) errors.first_name = tr('first_name_required');
            if (!values.last_name.trim()) errors.last_name = tr('last_name_required');
            if (!values.username.trim()) errors.username = tr('username_required');
            if (!passwordRules(values.password, values.password_confirm).slice(0, 4).every(rule => rule[1])) errors.password = tr('password_rules_error');
            if (!values.password_confirm) errors.password_confirm = tr('password_confirm_required');
            else if (values.password !== values.password_confirm) errors.password_confirm = tr('password_error_match');
        }
        return errors;
    };

    const finishLogin = (res) => {
        localStorage.setItem("access_token", res.data.access_token);
        localStorage.setItem("refresh_token", res.data.refresh_token);
        localStorage.setItem("user", JSON.stringify(res.data.user));
        setUser(res.data.user);
        // a guest sent here from a protected page goes back to it
        navigate(location.state?.from || '/');
    };

    const failLogin = () => setAlert({ type: 'error', text: t('login_error') });

    const login = () => axiosInstance
        .post(`api/auth/login/`, { grant_type: "password", username: email, password: values.password })
        .then(finishLogin)
        .catch(err => {
            setLoading(false);
            const reasons = err.response?.status === 400 ? err.response.data?.non_field_errors || [] : [];
            if (reasons.includes("E-mail is not verified.")) setAlert({ type: 'warn', text: t('email_not_verified'), resend: true });
            else if (reasons.includes("Unable to log in with provided credentials.")) setAlert({ type: 'error', text: t('wrong_credencials') });
            else failLogin();
        });

    const register = () => UserAPI
        .register({
            email,
            username: values.username.trim(),
            password1: values.password,
            password2: values.password_confirm,
            first_name: values.first_name.trim(),
            last_name: values.last_name.trim(),
        })
        .then(() => setDone(true))
        .catch(err => {
            const data = err.response?.status === 400 ? err.response.data : null;
            const errors = {};
            if (data?.password1) errors.password = [].concat(data.password1).map(item => tr(item)).join(' ');
            if (data?.email) errors.email = [].concat(data.email).includes("Enter a valid email address.") ? tr('email_not_valid') : tr('email_exists');
            if (data?.username) errors.username = tr('username_exists');
            if (Object.keys(errors).length > 0) setServerErrors(errors);
            else setAlert({ type: 'error', text: tr('registration_error') });
        })
        .finally(() => setLoading(false));

    const resetPassword = () => UserAPI
        .passwordReset({ email })
        .then(() => setDone(true))
        .catch(() => setAlert({ type: 'error', text: t('reset_password_err') }))
        .finally(() => setLoading(false));

    const handleSubmit = (e) => {
        e.preventDefault();
        if (loading) return;
        setTried(true);
        setAlert(null);
        setServerErrors({});
        if (Object.keys(validate()).length > 0) return;
        setLoading(true);
        if (isLogin) login();
        else if (isRegister) register();
        else resetPassword();
    };

    const resend = () => UserAPI
        .resendVerificationMail({ email })
        .then(() => setResent('ok'))
        .catch(() => setResent('error'));

    const responseFacebook = (response) => {
        if (!response?.accessToken) return;
        FacebookLogin(response.accessToken).then(finishLogin).catch(failLogin);
    };
    const responseGoogle = (response) => gLogin(response.accessToken).then(finishLogin).catch(failLogin);
    const googleFailed = (response) => {
        if (response?.error !== 'popup_closed_by_user') failLogin();
    };

    const errors = { ...(tried ? validate() : {}), ...serverErrors };
    const field = (name, label, placeholder, icon, props = {}) => (
        <Field name={name} label={label} placeholder={placeholder} icon={icon} value={values[name]}
            onChange={handleChange} error={errors[name]} {...props} />
    );
    const resendNote = resent &&
        <span className={`hs-auth-resent ${resent}`}>{tr(resent === 'ok' ? 'resend_success' : 'resend_error')}</span>;
    const title = isLogin ? t('login_title') : isRegister ? tr('register_title') : t('forgot_title');
    const subtitle = isLogin ? t('login_subtitle') : isRegister ? tr('register_subtitle') : t('reset_password_text');
    const submitLabel = loading ? t(isLogin ? 'logging_in' : 'sending')
        : isLogin ? t('submit_login') : isRegister ? tr('submit_register') : t('submit_forgot');

    return (
        <div className='hs hs-auth'>
            <section className='hs-auth-form'>
                <div className='hs-auth-card'>
                    {done
                        ? <div className='hs-auth-done'>
                            <span className='hs-auth-done-icon'><Icon d={ICONS.mail} size={28} /></span>
                            <h1>{isRegister ? tr('done_title') : t('forgot_done_title')}</h1>
                            <p>{isRegister ? tr('done_text') : t('reset_password_success')}</p>
                            <span className='hs-auth-done-mail'>{email}</span>
                            <Link to='/login' className='hs-auth-dark'>{t('back_to_login')}</Link>
                            {isRegister && (resendNote || <button className='hs-auth-text-btn' onClick={resend}>{tr('resend_mail')}</button>)}
                        </div>
                        : <form className='hs-auth-body' onSubmit={handleSubmit} noValidate>
                            {!isForgot &&
                                <div className='hs-auth-tabs'>
                                    <Link to='/login' state={location.state} replace className={isLogin ? 'on' : ''}>{t('login')}</Link>
                                    <Link to='/register' state={location.state} replace className={isRegister ? 'on' : ''}>{t('register')}</Link>
                                </div>}

                            <div className='hs-auth-heading'>
                                {isForgot && <Link to='/login' className='hs-auth-back'>← {t('back_to_login')}</Link>}
                                <h1>{title}</h1>
                                <p>{subtitle}</p>
                            </div>

                            {alert &&
                                <div role='alert' className={`hs-auth-alert ${alert.type}`}>
                                    <Icon d={ICONS.alert} width={2.2} />
                                    <span>
                                        {alert.text}
                                        {alert.resend && (resendNote || <button type='button' className='hs-auth-text-btn' onClick={resend}>{tr('resend_mail')}</button>)}
                                    </span>
                                </div>}

                            <div className='hs-auth-fields'>
                                {isRegister && <>
                                    {field('first_name', tr('first_name'), tr('first_name_hint'), ICONS.user, { half: true, autoComplete: 'given-name' })}
                                    {field('last_name', tr('last_name'), tr('last_name_hint'), ICONS.user, { half: true, autoComplete: 'family-name' })}
                                    {field('username', tr('username'), tr('username_hint'), ICONS.at, { autoComplete: 'username' })}
                                </>}
                                {field('email', t('email'), t('email-hint'), ICONS.mail, { type: 'email', autoComplete: 'email' })}
                                {!isForgot && field('password', t('password'), t('password-hint'), ICONS.lock,
                                    { type: 'password', half: isRegister, autoComplete: isLogin ? 'current-password' : 'new-password' })}
                                {isRegister && field('password_confirm', tr('password_confirm'), tr('password_confirm_hint'), ICONS.lock,
                                    { type: 'password', half: true, autoComplete: 'new-password' })}
                            </div>

                            {isRegister &&
                                <div className='hs-auth-rules'>
                                    {passwordRules(values.password, values.password_confirm).map(([key, ok]) => (
                                        <span key={key} className={ok ? 'ok' : ''}>
                                            <span className='hs-auth-rule-dot'><Icon d={ICONS.check} size={10} width={3.6} /></span>
                                            {tr(key)}
                                        </span>
                                    ))}
                                </div>}

                            {isLogin && <Link to='/password/reset' className='hs-auth-forgot'>{t('forgotten_pass')}</Link>}

                            <button type='submit' className='hs-auth-submit' disabled={loading}>
                                {loading && <span className='hs-auth-spinner'></span>}
                                {submitLabel}
                            </button>

                            {!isForgot && <>
                                <div className='hs-auth-divider'><span></span>{isLogin ? t('social_login') : tr('social_register')}<span></span></div>
                                <div className='hs-auth-socials'>
                                    <GoogleLogin clientId={GOOGLE_CLIENT_ID} onSuccess={responseGoogle} onFailure={googleFailed} cookiePolicy={"single_host_origin"}
                                        render={(google) => (
                                            <button type='button' className='hs-auth-social' onClick={google.onClick} disabled={google.disabled}>
                                                <GoogleIcon />Google
                                            </button>
                                        )} />
                                    <FbLogin appId={FACEBOOK_APP_ID} fields="first_name, last_name, email" callback={responseFacebook}
                                        cssClass='hs-auth-social' textButton='Facebook' icon={<FacebookIcon />} />
                                </div>
                                <p className='hs-auth-switch'>
                                    {isLogin ? t('no_account') : tr('have_account')}{' '}
                                    <Link to={isLogin ? '/register' : '/login'} state={location.state} replace>{isLogin ? t('register_link') : tr('login_link')}</Link>
                                </p>
                            </>}
                        </form>}
                </div>
                <p className='hs-auth-terms'>
                    {t('terms_prefix')} <Link to='/terms'>{t('terms')}</Link> {t('terms_and')} <Link to='/privacy'>{t('privacy')}</Link>.
                </p>
            </section>

            <aside className='hs-auth-visual' style={{ backgroundImage: `url(${heroImage})` }}>
                <div className='hs-auth-visual-text'>
                    <h2>{t('visual_title')}</h2>
                    {PERKS.map(key => (
                        <span key={key} className='hs-auth-perk'>
                            <span className='hs-auth-perk-dot'><Icon d={ICONS.check} size={12} width={3.2} /></span>
                            {t(key)}
                        </span>
                    ))}
                </div>
            </aside>
        </div>
    );
};
